import memoize from 'lodash/memoize';
import { makePathModel, asTypeProxy } from '../../../../utils/nestedPathModel';
import translations from './translations';
import { PURPOSES_OF_USE, OBJECT_GROUP_TYPE, TYPES_OF_USAGE, TIME_OF_DEMAND, } from './formModel.types';
import { getTranslation } from '../../../../utils/translations';
import constants from './constants';
import { Schema } from '../../../../adapters/schema';
import { makePostalAddressSubformModel } from '../../../../subforms/PostalAddressSubForm/formModel';
var postalAddressSubformModel = makePostalAddressSubformModel();
var t = getTranslation(translations);
var initialValues = {
    loanTimeOfDemand: TIME_OF_DEMAND.AT_ONCE,
    typeOfUse: TYPES_OF_USAGE.SELF_USED,
    objectGroupType: OBJECT_GROUP_TYPE.HOUSE,
    isPostalAddressKnown: true,
    isExistingLoanPresented: false,
    purposeOfUse: PURPOSES_OF_USE.PURCHASE_EXISTING_HOME,
};
var fields = asTypeProxy();
var PATHS = {
    LOAN_AMOUNT: fields.loanAmount,
    LOAN_TIME_OF_DEMAND: fields.loanTimeOfDemand,
    AVAILABLE_EQUITY: fields.availableEquity,
    OBJECT_TYPE: fields.objectType,
    OBJECT_TYPE_OF_USE: fields.typeOfUse,
    PURPOSE_OF_USE: fields.purposeOfUse,
    AREA_IN_SQM: fields.areaInSqm,
    YEAR_OF_CONSTRUCTION: fields.yearOfConstruction,
    MARKET_VALUE: fields.marketValue,
    BASE_COST: fields.baseCost,
    ADDITIONAL_COST: fields.additionalCost,
    OBJECT_GROUP_TYPE: fields.objectGroupType,
    IS_EXISTING_LOAN_PRESENTED: fields.isExistingLoanPresented,
    FIXED_INTEREST_RATE_UNTIL: fields.fixedInterestRateUntil,
    RESIDUAL_DEBT: fields.residualDebt,
    MONTHLY_REPAYMENT: fields.monthlyRepayment,
    IS_POSTAL_ADDRESS_KNOWN: fields.isPostalAddressKnown,
    POSTAL_ADDRESS: fields.postalAddress,
    PROJECT_STATUS: fields.projectStatus,
};
var _a = makePathModel(PATHS), paths = _a[0], selectors = _a[1];
var makeSchemaForCurrencyField = function (label, min, max) {
    return Schema.number().label(label).required().min(min).max(max);
};
function makeSchemaForCostField(label) {
    return makeSchemaForCurrencyField(label, 1, 99999999);
}
// @todo: Schema.schemaOf<ProjectFields> should be applied here
var validationSchema = Schema.object().shape({
    loanAmount: makeSchemaForCurrencyField(t.loanAmountLabel, 1000, 99999999),
    availableEquity: Schema.number()
        .label(t.availableEquityLabel)
        .min(0)
        .max(99999999),
    areaInSqm: Schema.number().label(t.areaInSqmLabel),
    yearOfConstruction: Schema.number().label(t.yearOfConstructionLabel),
    marketValue: Schema.mixed().when(['$values'], {
        is: function (values) { return helpers.shouldShowMarketValue(values); },
        then: function () {
            return Schema.number()
                .label(t.marketValueLabel)
                .required()
                .min(1000)
                .max(99999999);
        },
    }),
    baseCost: Schema.mixed()
        .when('purposeOfUse', {
        is: PURPOSES_OF_USE.PURCHASE_EXISTING_HOME,
        then: makeSchemaForCostField(t.baseCostLabel_buyer),
    })
        .when('purposeOfUse', {
        is: PURPOSES_OF_USE.CONSTRUCTION,
        then: makeSchemaForCostField(t.baseCostLabel_owner),
    }),
    additionalCost: Schema.mixed()
        .when(['$values'], {
        is: function (values) {
            return helpers.shouldShowAdditionalCostForModernization(values) ||
                helpers.shouldShowAdditionalCostForHomeModernization(values);
        },
        then: Schema.number()
            .label(t.additionalCost_modernization)
            .min(0)
            .max(99999999),
    })
        .when(['$values'], {
        is: function (values) {
            return helpers.shouldShowAdditionalCostForLandPrice(values);
        },
        then: makeSchemaForCostField(t.additionalCost_landPrice),
    }),
    postalAddress: Schema.object().when(['$values'], {
        is: function (values) {
            return helpers.shouldShowKnownPropertyFields(values);
        },
        then: postalAddressSubformModel.validationSchema,
    }),
    fixedInterestRateUntil: Schema.mixed().when(['$values'], {
        is: function (values) { return helpers.isExistingLoanPresented(values); },
        then: Schema.date().label(t.fixedInterestRateUntilLabel).required(),
    }),
    residualDebt: Schema.mixed().when(['$values'], {
        is: function (values) { return helpers.isExistingLoanPresented(values); },
        then: Schema.number().label(t.residualDebtLabel).required().min(1),
    }),
    monthlyRepayment: Schema.mixed().when(['$values'], {
        is: function (values) { return helpers.isExistingLoanPresented(values); },
        then: Schema.number().label(t.monthlyRepaymentLabel).required().min(1),
    }),
});
var makeMemoizedPurposeOfUseOptions = memoize(function (objectGroupType) {
    switch (objectGroupType) {
        case OBJECT_GROUP_TYPE.APARTMENTS:
            return constants.purposeOfUseOptions.filter(function (_a) {
                var value = _a.value;
                return value !== PURPOSES_OF_USE.CONSTRUCTION;
            });
        case OBJECT_GROUP_TYPE.PROPERTY:
            return constants.purposeOfUseOptions.filter(function (_a) {
                var value = _a.value;
                return value !== PURPOSES_OF_USE.CONSTRUCTION &&
                    value !== PURPOSES_OF_USE.MODERNIZATION;
            });
        case OBJECT_GROUP_TYPE.HOUSE:
        default:
            return constants.purposeOfUseOptions;
    }
});
var helpers = {
    isAddressRequired: function (values) {
        return constants.purposeOfUseWithRequiredAddress.includes(selectors.PURPOSE_OF_USE(values));
    },
    shouldShowIsPostalAddressKnown: function (values) {
        return !helpers.isAddressRequired(values);
    },
    shouldShowRealEstateData: function (values) {
        var objectGroupType = selectors.OBJECT_GROUP_TYPE(values);
        return (objectGroupType === OBJECT_GROUP_TYPE.HOUSE ||
            objectGroupType === OBJECT_GROUP_TYPE.APARTMENTS);
    },
    shouldSetProjectStatus: function (values) {
        var purposesOfUseToShowField = [
            PURPOSES_OF_USE.PURCHASE_EXISTING_HOME,
            PURPOSES_OF_USE.CONSTRUCTION,
        ];
        return purposesOfUseToShowField.includes(selectors.PURPOSE_OF_USE(values));
    },
    shouldShowProjectStatus: function (values) {
        return (helpers.shouldSetProjectStatus(values) &&
            !!selectors.IS_POSTAL_ADDRESS_KNOWN(values));
    },
    getProjectStatusOptions: function (values) {
        if (selectors.PURPOSE_OF_USE(values) === PURPOSES_OF_USE.CONSTRUCTION) {
            return constants.projectStatusOptionsForConstruction;
        }
        return selectors.OBJECT_GROUP_TYPE(values) === OBJECT_GROUP_TYPE.PROPERTY
            ? constants.projectStatusOptionsForPropertyPurchase
            : constants.projectStatusOptionsForPurchase;
    },
    shouldShowAreaInSqm: function (values) {
        return (helpers.shouldShowRealEstateData(values) &&
            helpers.shouldShowKnownPropertyFields(values));
    },
    shouldShowYearOfConstruction: function (values) {
        if (!helpers.shouldShowRealEstateData(values) ||
            !helpers.shouldShowKnownPropertyFields(values)) {
            return false;
        }
        var requiredPurposesOfUse = [
            PURPOSES_OF_USE.PROLONGATION,
            PURPOSES_OF_USE.PROCUREMENT,
            PURPOSES_OF_USE.MODERNIZATION,
            PURPOSES_OF_USE.PURCHASE_EXISTING_HOME,
        ];
        return requiredPurposesOfUse.includes(selectors.PURPOSE_OF_USE(values));
    },
    shouldShowMarketValue: function (values) {
        if (!helpers.shouldShowRealEstateData(values)) {
            return false;
        }
        var requiredPurposesOfUse = [
            PURPOSES_OF_USE.PROLONGATION,
            PURPOSES_OF_USE.PROCUREMENT,
            PURPOSES_OF_USE.MODERNIZATION,
        ];
        return requiredPurposesOfUse.includes(selectors.PURPOSE_OF_USE(values));
    },
    shouldShowBaseCostField: function (values) {
        var purposeOfUse = selectors.PURPOSE_OF_USE(values);
        return (purposeOfUse === PURPOSES_OF_USE.CONSTRUCTION ||
            purposeOfUse === PURPOSES_OF_USE.PURCHASE_EXISTING_HOME);
    },
    shouldShowAdditionalCostForLandPrice: function (values) {
        return selectors.PURPOSE_OF_USE(values) === PURPOSES_OF_USE.CONSTRUCTION;
    },
    shouldShowAdditionalCostForModernization: function (values) {
        return selectors.PURPOSE_OF_USE(values) === PURPOSES_OF_USE.MODERNIZATION;
    },
    shouldShowAdditionalCostForHomeModernization: function (values) {
        var objectTypes = [
            OBJECT_GROUP_TYPE.HOUSE,
            OBJECT_GROUP_TYPE.APARTMENTS,
        ];
        var selectedObjectGroupType = selectors.OBJECT_GROUP_TYPE(values);
        var isRequiredObjectType = objectTypes.includes(selectedObjectGroupType);
        var isRequiredPurposeOfUse = selectors.PURPOSE_OF_USE(values) ===
            PURPOSES_OF_USE.PURCHASE_EXISTING_HOME;
        return isRequiredPurposeOfUse && isRequiredObjectType;
    },
    shouldShowAdditionalCost: function (values) {
        return (helpers.shouldShowAdditionalCostForLandPrice(values) ||
            helpers.shouldShowAdditionalCostForModernization(values) ||
            helpers.shouldShowAdditionalCostForHomeModernization(values));
    },
    isAdditionalCostOptional: function (values) {
        return (helpers.shouldShowAdditionalCostForModernization(values) ||
            helpers.shouldShowAdditionalCostForHomeModernization(values));
    },
    shouldShowObjectGroupTypeField: function (values) {
        var objectGroupTypes = [
            OBJECT_GROUP_TYPE.HOUSE,
            OBJECT_GROUP_TYPE.APARTMENTS,
        ];
        return objectGroupTypes.includes(selectors.OBJECT_GROUP_TYPE(values));
    },
    shouldShowExistingLoanBlock: function (values) {
        var requiredPurposesOfUse = [
            PURPOSES_OF_USE.PROCUREMENT,
            PURPOSES_OF_USE.MODERNIZATION,
            PURPOSES_OF_USE.PROLONGATION,
        ];
        return requiredPurposesOfUse.includes(selectors.PURPOSE_OF_USE(values));
    },
    isExistingLoanPresentedFieldRequired: function (values) {
        return selectors.PURPOSE_OF_USE(values) === PURPOSES_OF_USE.PROLONGATION;
    },
    isExistingLoanPresented: function (values) {
        return (helpers.shouldShowExistingLoanBlock(values) &&
            !!selectors.IS_EXISTING_LOAN_PRESENTED(values));
    },
    shouldShowHouseTypeField: function (values) {
        return selectors.OBJECT_GROUP_TYPE(values) === OBJECT_GROUP_TYPE.HOUSE;
    },
    shouldShowKnownPropertyFields: function (values) {
        return selectors.IS_POSTAL_ADDRESS_KNOWN(values) || false;
    },
    getLabelForBaseCostField: function (values) {
        switch (selectors.PURPOSE_OF_USE(values)) {
            case PURPOSES_OF_USE.MODERNIZATION:
                return t.baseCostLabel_modernizator;
            case PURPOSES_OF_USE.PURCHASE_EXISTING_HOME:
                return t.baseCostLabel_buyer;
            default:
                return t.baseCostLabel_owner;
        }
    },
    getLabelForAdditionalPriceField: function (values) {
        if (helpers.shouldShowAdditionalCostForLandPrice(values)) {
            return t.additionalCost_landPrice;
        }
        if (helpers.shouldShowAdditionalCostForModernization(values) ||
            helpers.shouldShowAdditionalCostForHomeModernization(values)) {
            return t.additionalCost_modernization;
        }
        return '';
    },
    getDynamicPurposeOfUseOptions: function (values) {
        return makeMemoizedPurposeOfUseOptions(selectors.OBJECT_GROUP_TYPE(values));
    },
};
export var projectPageModel = {
    paths: paths,
    selectors: selectors,
    helpers: helpers,
    initialValues: initialValues,
    validationSchema: validationSchema,
};
